// A6 — THE MEETING OPENER. Brief 65 (brief 50 §4), the fifth of the sixteen.
//
// Opening the meeting dialog on "now": today's date, the start rounded UP to
// the next quarter hour, an end an hour later — two if that rounding crossed
// the hour — and the four constants every seed carries.
//
//   src/App.tsx:5156-5178@8fdbbd6c x src/views/CollectiveCaseView.tsx:4699-4733@8fdbbd6c  73.0%
//
// ONE LIVE PAIR, NOT TWO. The census reports a third block,
// `CollectiveCaseView.tsx:3951-3978@8fdbbd6c`, at the same 73.0% — and it sits
// under the `{false &&` at CollectiveCaseView.tsx:3862@8fdbbd6c. Brief 54's rule
// is that a dead block does not enter the sweep, so it is left exactly as it
// stands: untouched, still dead, still its own copy. Deleting it is register
// 54a and Laurent's.
//
// 73% MEANS A QUARTER DIFFERS, AND §2 IS WHERE THAT GETS DECIDED. What differs
// is entirely the SEED's identity fields — `internal`/`kind`/`caseObj`/
// `attendees`/`title` — and what is shared is the arithmetic plus the constant
// tail. So the seam is a spread: each caller passes the fields that identify
// its meeting, and this supplies the time and the constants. Nothing is chosen
// between the two copies, because the two copies never disagreed about
// anything they both had.
//
// The key ORDER of the returned object is each original's order — the caller's
// fields first, then date/start/end/location/notes/mode/platform — so the seed
// is the same object it was, not merely an equivalent one.

/* 123 §3 — THE MEETING TYPE IS AN ARGUMENT NOW, NOT A LITERAL. `meetingType` is
   `firstMeetingTypeCode(evtEntries)` (utils/taxonomyMap) — the firm's own first
   meeting type, in taxonomy order — and `null` when the firm has flagged none,
   in which case no `type` is seeded at all and the user picks one. The key ORDER
   of the returned object is unchanged, which the A6 census above depends on. */
export function meetingSeed(
  identity: Record<string, unknown>,
  mtgDefLoc: unknown,
  meetingType: string | null,
  /* BRIEF 124 §1 — the mode that type DECLARES, resolved by the host from the
     same fold everything else reads (`meetingDefaultModes`). Absent or null
     keeps `"person"` below, so the seeded taxonomy's behaviour is byte-identical
     — REU declares `person` and this button has always opened In Person. A firm
     whose first meeting type is an ONLINE one now gets its own answer instead of
     ours, which is the whole §. */
  defaultMode?: "person" | "online" | null,
) {
  const td = new Date();
  const todS = td.toISOString().slice(0, 10);
  const nowH = String(td.getHours()).padStart(2, "0");
  const nowM = String((Math.ceil(td.getMinutes() / 15) * 15) % 60).padStart(
    2,
    "0",
  );
  const endH = String(
    td.getHours() + (Math.ceil(td.getMinutes() / 15) * 15 >= 60 ? 2 : 1),
  ).padStart(2, "0");
  return {
    ...identity,
    date: todS,
    /* ── BRIEF 120 §3 — THE KEYS ARE THE EVENT MODAL'S NOW ────────────────────
       The arithmetic above is untouched and keeps its job; only the names it
       hands back change, because the surface it seeds changed. `startTime` is
       the ag modal's `time`; `mode: "person"` / `platform: "teams"` become
       `type: "REU"`, because Table 1's first row rules that THE TYPE FIELD IS
       THE TOGGLE — `EV_MEETING_KIND` (App.tsx:504@50fb417c) maps REU to the room leg and
       VIR to the virtual one, so a second switch meaning the same thing is
       exactly what §3 forbids. `platform` goes rather than moving: it is only
       ever read on the virtual leg, and this seed opens the in-person one.

       BRIEF 123 §3 — AND THE `mode` KEY COMES BACK, because 120 was wrong about
       WHICH switch the type was. The type says what kind of event; the mode says
       how it is held. So the seed carries BOTH: the firm's own first meeting
       type (passed in, never `"REU"` written down) and `meetingMode: "person"`,
       which is what this button has always opened on — the dialog it replaced
       opened on In Person too. */
    time: nowH + ":" + nowM,
    endTime: endH + ":" + nowM,
    location: mtgDefLoc,
    notes: "",
    ...(meetingType ? { type: meetingType } : {}),
    meetingMode: defaultMode || "person",
  };
}

/* ── BRIEF 119 §1c — WHAT THE MEETING SEED READS OFF AN EVENT ────────────────
   Written out rather than `any` for `ratchets.js`'s reason: a counter that may
   only shrink is what stops new code paying for old habits. It lives HERE, not
   in App, because two modules need it and a type imported from App would be a
   cycle. Every field is optional because the row arrives from two places — the
   modal's `form` before a save, and the server's row after one — and the two
   shapes are one type. */
export type OutlookAttendeeRef = { email?: string | null };
export type SeedableEvent = {
  id?: number | string | null;
  caId?: number | string | null;
  caseId?: number | string | null;
  ccId?: number | string | null;
  title?: string | null;
  type?: string | null;
  date?: string | null;
  time?: string | null;
  endTime?: string | null;
  location?: string | null;
  notes?: string | null;
  platform?: string | null;
  outlookAttendees?: OutlookAttendeeRef[] | null;
  /* 122 §2 — the collaborators on the event. Additive, and read rather than
     written here: `mergeAttendeeRows` needs the STORED assignment to decide a
     row's tick on an existing event, where the team's pre-check would otherwise
     resurrect somebody who was deliberately removed. Optional like every field
     above it, for the same reason — the row arrives both before and after a
     save. */
  assignedCollaborators?: (number | string)[] | null;
};
